import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Target, ListChecks, CheckSquare, Sparkles, ArrowRight, ArrowLeft } from 'lucide-react';
import { useFocusTrap } from '../../lib/useFocusTrap';

/**
 * OnboardingWizard — mini-tour mostrato la prima volta che un utente
 * apre la Dashboard senza obiettivi.
 *
 * 4 slide:
 *  1. Welcome  — cos'è Quantum Planner
 *  2. Obiettivi — Anno → Quarter → Mese → Settimana
 *  3. MIT       — Most Important Tasks giornaliere
 *  4. CTA       — pulsante "Vai al Setup" (porta a /setup)
 *
 * Trigger: rendered solo se prop `open=true`. La Dashboard lo apre quando
 * `!obiettivi.length && localStorage[qp.onboarding.completed] !== 'true'`.
 * Chiusura (X o "Salta"): segna come completato (non riapre più).
 * "Vai al Setup" del 4° step: segna come completato + naviga.
 */
export default function OnboardingWizard({ open, onClose }) {
  const [step, setStep] = useState(0);
  const panelRef = useRef(null);

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  // Il wizard non aveva gestione Esc: la delega al focus trap (onEscape).
  useFocusTrap(open, panelRef, { onEscape: onClose });

  if (!open) return null;

  const steps = [
    {
      icon: <Sparkles size={28} strokeWidth={1.5} className="text-accent" />,
      title: 'Benvenuto in Quantum Planner',
      body: (
        <>
          <p>
            Quantum Planner ti aiuta a passare da <strong>obiettivi annuali grandi</strong> ad
            <strong> azioni quotidiane concrete</strong> senza perderti per strada.
          </p>
          <p className="mt-3">
            QuantumCoach — un coach AI — ti accompagna nei momenti chiave: setup annuale,
            pianificazione mensile, review settimanale.
          </p>
        </>
      ),
    },
    {
      icon: <Target size={28} strokeWidth={1.5} className="text-area-business" />,
      title: 'Obiettivi a cascata',
      body: (
        <>
          <p>Ogni Obiettivo che definisci si articola in <strong>Key Result</strong> numerici, decomposti automaticamente per:</p>
          <ul className="mt-3 space-y-1 ml-4 list-disc text-sm">
            <li>Quarter (Q1 → Q4)</li>
            <li>Mese</li>
            <li>Settimana ISO</li>
          </ul>
          <p className="mt-3 text-sm text-soft">
            Aggiorni il numero reale ogni settimana, la dashboard ti dice se sei on-track o no.
          </p>
        </>
      ),
    },
    {
      icon: <CheckSquare size={28} strokeWidth={1.5} className="text-ok" />,
      title: 'MIT — Most Important Tasks',
      body: (
        <>
          <p>
            Le <strong>MIT</strong> sono i 1-3 task del giorno che fanno davvero la differenza.
            Si associano a un Key Result così sai esattamente <em>perché</em> li stai facendo.
          </p>
          <p className="mt-3">
            Chiudere MIT ogni settimana costruisce uno <strong>streak</strong>: una metrica
            di costanza che vedrai crescere nella Dashboard.
          </p>
        </>
      ),
    },
    {
      icon: <ListChecks size={28} strokeWidth={1.5} className="text-area-personale" />,
      title: 'Partiamo dal Setup',
      body: (
        <>
          <p>
            Il primo passo è il <strong>Setup annuale</strong>: definisci un tema per l&apos;anno,
            poi QuantumCoach ti guida nel proporre i primi obiettivi.
          </p>
          <p className="mt-3 text-sm text-soft">
            Tutto è conversazionale: scrivi al coach, lui propone, tu confermi o annulli.
            Niente form complessi.
          </p>
        </>
      ),
    },
  ];

  const cur = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      data-testid="onboarding-wizard"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-title"
    >
      <div ref={panelRef} className="w-full max-w-xl bg-paper border border-line shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-linesoft">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-mute">
            Tour · {step + 1} / {steps.length}
          </span>
          <button
            onClick={onClose}
            className="text-mute hover:text-ink"
            aria-label="Chiudi tour"
            data-testid="onboarding-close"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-8 min-h-[280px]">
          <div className="mb-5 inline-flex items-center justify-center w-14 h-14 rounded-full bg-canvas border border-linesoft">
            {cur.icon}
          </div>
          <h2 id="onboarding-title" className="font-display text-2xl tracking-tight mb-3">
            {cur.title}
          </h2>
          <div className="font-body text-[15px] leading-relaxed text-ink space-y-1">
            {cur.body}
          </div>
        </div>

        {/* Progress dots */}
        <div className="px-6 pb-3 flex items-center gap-1.5">
          {steps.map((_, i) => (
            <span
              key={`step-${i}`}
              className={
                'h-1.5 transition-all rounded-full ' +
                (i === step ? 'w-6 bg-ink' : 'w-1.5 bg-line')
              }
            />
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-linesoft bg-canvas">
          <button
            onClick={() => (step === 0 ? onClose() : setStep((s) => s - 1))}
            className="font-mono text-[11px] uppercase tracking-[0.18em] text-soft hover:text-ink inline-flex items-center gap-1.5"
            data-testid="onboarding-back"
          >
            {step === 0 ? (
              'Salta'
            ) : (
              <>
                <ArrowLeft size={12} strokeWidth={1.8} /> Indietro
              </>
            )}
          </button>

          {isLast ? (
            <Link
              to="/setup"
              onClick={onClose}
              className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-canvas font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-accent transition-colors"
              data-testid="onboarding-go-setup"
            >
              Vai al Setup <ArrowRight size={12} strokeWidth={1.8} />
            </Link>
          ) : (
            <button
              onClick={() => setStep((s) => s + 1)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-canvas font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-accent transition-colors"
              data-testid="onboarding-next"
            >
              Avanti <ArrowRight size={12} strokeWidth={1.8} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
